import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PaperPlaneTilt, Eye, Megaphone, ArrowClockwise, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const TEMPLATES = {
  whats_new_may_2026: {
    subject: "What's new in FlashGrants — Sample-ask scripts, Sponsorship Proposals & more",
    heading: "Big update for FlashGrants — built for you",
    cta_text: "Open Corporate Scout",
    cta_url: "https://flash-grants.com/corporate-scout",
    body_html: `<p>Hi there,</p>
<p>Quick update from the FlashGrants team — a few new features just shipped that are designed to help you actually <em>collect</em> the corporate giving money your nonprofit is leaving on the table.</p>

<h3 style="color:#10B981;margin-top:24px;margin-bottom:6px;font-size:16px;">1. Sample ask scripts for matching gifts</h3>
<p>Most SC nonprofits leave $3K-$8K of matching-gift money on the table every year because no one knows to ask the donor to submit a match request. Now, every Corporate Scout matching-gift program comes with a one-click <strong>"Show sample ask script"</strong> button — a 6-line copy/paste-ready ask that's pre-filled with your nonprofit's name, the company's match ratio, and the program URL. Send it after every donation from a matching-company employee. Your dollars effectively double.</p>

<h3 style="color:#10B981;margin-top:24px;margin-bottom:6px;font-size:16px;">2. Sponsorship Proposal Generator</h3>
<p>Every Corporate Scout card now has a <strong>"Sponsorship Proposal"</strong> button (Premium). Tell us what you're raising money for and the target budget, and we'll write a 3-tier (Bronze / Silver / Gold) proposal anchored to that company's documented CSR priorities — ready to paste into an email. Turns a "begging letter" into a business proposal in 10 seconds.</p>

<h3 style="color:#10B981;margin-top:24px;margin-bottom:6px;font-size:16px;">3. Find Decision Maker on LinkedIn</h3>
<p>One-click button on every Scout card opens a LinkedIn People search pre-filled for that company's CSR / Community Relations team in South Carolina. Saves you 5 minutes of guessing per company.</p>

<h3 style="color:#10B981;margin-top:24px;margin-bottom:6px;font-size:16px;">4. Submit-Ready Score on every draft</h3>
<p>When you generate a Flash Draft, you'll see a 0-100 readiness gauge that grades your draft on 6 axes: required sections, unfilled placeholders, length, real dollar amounts, measurable outcomes, and funder alignment. Click "Refresh" any time as you edit to see your score climb.</p>

<h3 style="color:#10B981;margin-top:24px;margin-bottom:6px;font-size:16px;">5. Self-serve trial &amp; password reset</h3>
<p>Two quality-of-life upgrades: any new visitor can now start their own 14-day Premium trial directly from the homepage (card required, will not auto-charge), and there's a "Forgot password?" link on the sign-in screen if you ever get locked out.</p>

<p style="margin-top:24px;">Anything you'd like next? Just reply to this email — every reply lands directly in our inbox.</p>

<p>Onward,<br>The FlashGrants Team</p>`,
  },
};

const AUDIENCES_ORDER = [
  "admin_trial_users",
  "active_trial_users",
  "premium_users",
  "all_active_users",
];

const AdminAnnouncements = () => {
  const [counts, setCounts] = useState({});
  const [labels, setLabels] = useState({});
  const [history, setHistory] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  const [audience, setAudience] = useState("admin_trial_users");
  const [subject, setSubject] = useState("");
  const [heading, setHeading] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");

  const [previewing, setPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewRecipientCount, setPreviewRecipientCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const loadMeta = async () => {
    setLoadingMeta(true);
    try {
      const [c, h] = await Promise.all([
        axios.get(`${API}/admin/announcements/audience-counts`),
        axios.get(`${API}/admin/announcements/history`),
      ]);
      setCounts(c.data.counts || {});
      setLabels(c.data.labels || {});
      setHistory(h.data.history || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load admin data");
    } finally {
      setLoadingMeta(false);
    }
  };

  useEffect(() => { loadMeta(); }, []);

  const loadTemplate = (key) => {
    const t = TEMPLATES[key];
    if (!t) return;
    setSubject(t.subject);
    setHeading(t.heading || t.subject);
    setBodyHtml(t.body_html);
    setCtaText(t.cta_text || "");
    setCtaUrl(t.cta_url || "");
    toast.success("Template loaded — review and edit before sending");
  };

  const doDryRun = async () => {
    if (!subject || !bodyHtml) {
      toast.error("Subject and body are required");
      return null;
    }
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/admin/announcements/send`, {
        audience, subject, heading: heading || subject, body_html: bodyHtml, cta_text: ctaText || null, cta_url: ctaUrl || null, dry_run: true,
      });
      setPreviewHtml(res.data.rendered_html_preview || "");
      setPreviewRecipientCount(res.data.recipient_count);
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
      return null;
    } finally { setPreviewing(false); }
  };

  // Single-click "Send" handler.
  // - If the user hasn't previewed yet, we run the dry-run first to get the
  //   recipient count + rendered HTML, then open the confirmation modal.
  // - If they already previewed, we just open the modal.
  // The modal itself is what actually fires the real (dry_run=false) send.
  const handleSendClick = async () => {
    if (!previewHtml) {
      const result = await doDryRun();
      if (!result) return; // dry-run failed; toast already shown
    }
    setConfirming(true);
  };

  const doSend = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/announcements/send`, {
        audience, subject, heading: heading || subject, body_html: bodyHtml, cta_text: ctaText || null, cta_url: ctaUrl || null, dry_run: false,
      });
      toast.success(`Sent to ${res.data.sent_count} recipient${res.data.sent_count === 1 ? "" : "s"}`);
      setConfirming(false);
      loadMeta();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto py-6" data-testid="admin-announcements-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
              <Megaphone weight="fill" className="w-7 h-7 text-emerald-400" />
              Send Announcement
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Broadcast a product update or onboarding email to a specific audience. Sends via Resend (hello@flash-grants.com).
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={loadMeta} className="border-zinc-700 rounded-sm" data-testid="reload-counts-btn">
            <ArrowClockwise className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* Quick template loaders */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader><CardTitle className="text-base">Quick templates</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => loadTemplate("whats_new_may_2026")}
              className="border-emerald-500/40 text-emerald-300 rounded-sm hover:bg-emerald-500/10"
              data-testid="template-whats-new-may-2026"
            >
              Load "What's New — May 2026" template
            </Button>
          </CardContent>
        </Card>

        {/* Compose form */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader><CardTitle className="text-base">Compose</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Audience</Label>
              <div className="grid sm:grid-cols-2 gap-2" data-testid="audience-grid">
                {AUDIENCES_ORDER.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setAudience(key)}
                    className={`text-left p-3 rounded-sm border transition-colors ${
                      audience === key
                        ? "bg-emerald-500/10 border-emerald-500/40"
                        : "bg-zinc-900 border-zinc-800 hover:border-zinc-700"
                    }`}
                    data-testid={`audience-option-${key}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-zinc-100">{labels[key] || key}</span>
                      <span className="text-xs text-emerald-400 font-mono">
                        {loadingMeta ? "…" : `${counts[key] ?? 0} recipient${counts[key] === 1 ? "" : "s"}`}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Subject line</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What's new in FlashGrants…" className="bg-zinc-900 border-zinc-800" data-testid="announcement-subject-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Heading (H1 in email body)</Label>
                <Input value={heading} onChange={(e) => setHeading(e.target.value)} placeholder="Defaults to subject" className="bg-zinc-900 border-zinc-800" data-testid="announcement-heading-input" />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Body (HTML)</Label>
              <Textarea
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
                placeholder="<p>Hi there,</p><p>We just shipped...</p>"
                rows={14}
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                data-testid="announcement-body-input"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Plain HTML inside the FlashGrants email shell. Use &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;h3&gt;, &lt;a href=&quot;…&quot;&gt;. The header, footer, and CTA button are added automatically.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-zinc-300 mb-1.5 block">CTA button text <span className="text-zinc-500 font-normal">(optional)</span></Label>
                <Input value={ctaText} onChange={(e) => setCtaText(e.target.value)} placeholder="Open FlashGrants" className="bg-zinc-900 border-zinc-800" data-testid="announcement-cta-text-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-zinc-300 mb-1.5 block">CTA button URL <span className="text-zinc-500 font-normal">(optional)</span></Label>
                <Input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} placeholder="https://flash-grants.com/corporate-scout" className="bg-zinc-900 border-zinc-800" data-testid="announcement-cta-url-input" />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                onClick={doDryRun}
                disabled={previewing || !subject || !bodyHtml}
                variant="outline"
                className="border-zinc-700 rounded-sm"
                data-testid="announcement-preview-btn"
              >
                <Eye className="w-4 h-4 mr-1" />
                {previewing ? "Rendering…" : "Preview & Get Recipient Count"}
              </Button>
              <Button
                onClick={handleSendClick}
                disabled={previewing || !subject || !bodyHtml}
                className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                data-testid="announcement-send-btn"
              >
                <PaperPlaneTilt className="w-4 h-4 mr-1" />
                {previewing && !previewHtml
                  ? "Preparing…"
                  : `Send${previewRecipientCount !== null ? ` to ${previewRecipientCount} recipient${previewRecipientCount === 1 ? "" : "s"}` : ""}`}
              </Button>
            </div>
            {!previewHtml && (
              <p className="text-xs text-zinc-500 flex items-center gap-1">
                <Warning weight="fill" className="w-3.5 h-3.5 text-amber-400" />
                Tip: click "Preview" first to see the rendered email and exact recipient count. "Send" alone will preview + confirm in one step.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Live preview */}
        {previewHtml && (
          <Card className="bg-zinc-950 border-zinc-800" data-testid="announcement-preview-card">
            <CardHeader><CardTitle className="text-base">Email preview</CardTitle></CardHeader>
            <CardContent>
              <div className="bg-zinc-900 rounded-sm p-4 border border-zinc-800 overflow-x-auto">
                <iframe
                  title="Email preview"
                  srcDoc={previewHtml}
                  style={{ width: "100%", height: 580, border: "none", background: "#111", borderRadius: 6 }}
                  data-testid="announcement-preview-iframe"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Confirmation modal — centered Dialog so it's always visible regardless
            of where the user is scrolled on the page. Replaces the old inline
            Card that rendered below the 580px preview iframe (off-screen). */}
        <Dialog open={confirming} onOpenChange={setConfirming}>
          <DialogContent
            className="bg-zinc-950 border-zinc-800 max-w-md"
            data-testid="announcement-confirm-modal"
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-300">
                <Warning weight="fill" className="w-5 h-5 text-amber-400" />
                Confirm send
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-zinc-300 leading-relaxed">
                You're about to send{" "}
                <span className="text-emerald-400 font-bold">{previewRecipientCount ?? 0}</span>{" "}
                email{previewRecipientCount === 1 ? "" : "s"} to{" "}
                <span className="text-amber-300 font-semibold">{labels[audience] || audience}</span>.
              </p>
              <p className="text-xs text-zinc-500">
                Subject: <span className="text-zinc-300">{subject}</span>
              </p>
              <p className="text-xs text-zinc-500">
                This action is irreversible — emails go out immediately via Resend (hello@flash-grants.com).
              </p>
              <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirming(false)}
                  className="border-zinc-700 rounded-sm flex-1"
                  data-testid="announcement-cancel-send-btn"
                  disabled={sending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={doSend}
                  disabled={sending}
                  className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm flex-1"
                  data-testid="announcement-confirm-send-btn"
                >
                  {sending ? "Sending…" : `Yes, send ${previewRecipientCount ?? 0} email${previewRecipientCount === 1 ? "" : "s"}`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* History */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader><CardTitle className="text-base">Recent sends</CardTitle></CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-sm text-zinc-500">No announcements sent yet.</p>
            ) : (
              <ul className="divide-y divide-zinc-800" data-testid="announcement-history-list">
                {history.map((h, i) => (
                  <li key={i} className="py-3 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-200 truncate">{h.subject}</p>
                      <p className="text-xs text-zinc-500">
                        {new Date(h.sent_at).toLocaleString()} &middot; {h.audience} &middot; {h.recipient_count} recipient{h.recipient_count === 1 ? "" : "s"}
                      </p>
                    </div>
                    <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminAnnouncements;
